import { useWarehouse } from '~/composables/useWarehouse'
import { useLookupStore } from '~/stores/lookupStore'
import { getAccessToken } from '~/utils/authToken'
import { PUBLIC_PATHS } from '~/utils/publicPaths'

/**
 * Asks which warehouse, straight after log-in, at a site that has several
 * (utils/warehouses.ts). Everything else is narrowed to the answer, so no
 * work screen is reached without one.
 *
 * A site with one warehouse, or with none set up, is never asked: the tablet
 * takes the one there is, or carries on without the level.
 *
 * The gang list is what says which warehouses there are. It is read once per
 * page load. A tablet that already knows its list does not wait for it — the
 * read goes on behind — so a tablet out of range is not held at a blank
 * screen; one that has never read it waits a few seconds and no longer.
 */
const NOT_ASKED_ON = new Set([...PUBLIC_PATHS, '/warehouse'])

/** How long a tablet with no list of its own waits for one before carrying on. */
const FIRST_READ_MS = 4_000

let reading: Promise<void> | null = null

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server || NOT_ASKED_ON.has(to.path)) return
  // Nobody logged in: the log-in screen comes first, and asks nothing.
  if (!getAccessToken()) return

  const site = useWarehouse()
  if (!site.readThisPage.value) {
    reading ??= useLookupStore().fetchGangEntries(FIRST_READ_MS)
      .then(site.noteGangs)
      // No answer: the tablet carries on with the list it kept, if any.
      .catch(() => {})
      .finally(() => { reading = null })
    if (site.gangs.value.length === 0) await reading
  }

  if (site.mustChoose.value) {
    return navigateTo({ path: '/warehouse', query: to.path === '/' ? {} : { then: to.fullPath } })
  }
})
